/**
 * Calendar Page — Month view with event management
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '../../auth';
import GoogleSyncCard from '../GoogleSync/GoogleSyncCard';
import './CalendarPage.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Weekday keys used by the server's `workingHours` map (sun..sat).
const WEEKDAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' }
];
const DEFAULT_WORKING_HOURS = {
  mon: { start: '09:00', end: '17:00' },
  tue: { start: '09:00', end: '17:00' },
  wed: { start: '09:00', end: '17:00' },
  thu: { start: '09:00', end: '17:00' },
  fri: { start: '09:00', end: '17:00' },
  sat: null,
  sun: null
};
const DEFAULT_SETTINGS = {
  timezone: 'America/Los_Angeles',
  slotDuration: 30,
  bufferMinutes: 15,
  minNoticeMinutes: 60,
  maxAdvanceDays: 60,
  holdExpiryMinutes: 1440,
  workingHours: DEFAULT_WORKING_HOURS,
  notifyViaEmail: true,
  notifyViaSms: false,
  bookingEnabled: false,
  bookingTitle: '',
  bookingDescription: '',
  bookingSlug: ''
};

// Timezone-safe local calendar date (YYYY-MM-DD) for an event, computed in the
// calendar owner's configured timezone — NOT the viewer's device timezone and NOT
// a naive UTC string slice. A timed event at 23:30 local must land on the local day,
// not roll to the next UTC day. All-day / date-only values are tz-independent.
const eventDateKey = (ev, tz) => {
  const s = (ev && ev.dtstart) || '';
  if ((ev && ev.allDay) || /^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (isNaN(d.getTime())) return s.slice(0, 10);
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(d);
  } catch (e) {
    return s.slice(0, 10);
  }
};

// Event editor in its own component with LOCAL form state. Keeping the form state here
// (instead of in CalendarPage) means typing only re-renders this modal — NOT the whole
// month grid — which was the cause of the laggy, unresponsive editor on phones.
function EventModal({ editEvent, defaultDateStr, onClose, onSaved }) {
  const [form, setForm] = useState(() => editEvent ? {
    summary: editEvent.summary || '', dtstart: (editEvent.dtstart || '').slice(0, 16), dtend: (editEvent.dtend || '').slice(0, 16),
    location: editEvent.location || '', description: editEvent.description || '', allDay: editEvent.allDay || false,
    generateMeetingLink: !!editEvent.meetingLink, meetingLink: editEvent.meetingLink || ''
  } : {
    summary: '', dtstart: `${defaultDateStr}T09:00`, dtend: `${defaultDateStr}T09:30`,
    location: '', description: '', allDay: false, generateMeetingLink: false, meetingLink: ''
  });
  const [saving, setSaving] = useState(false);

  const saveEvent = async () => {
    setSaving(true);
    try {
      if (editEvent) {
        const res = await authFetch(`/api/calendar/events/${editEvent.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'If-Match': `"${editEvent.etag}"` },
          body: JSON.stringify({ ...form, generateMeetingLink: form.generateMeetingLink || !!form.meetingLink, dtstart: new Date(form.dtstart).toISOString(), dtend: new Date(form.dtend).toISOString() })
        });
        if (!res.ok) { const d = await res.json(); alert(d.error); setSaving(false); return; }
      } else {
        const res = await authFetch('/api/calendar/events', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, generateMeetingLink: form.generateMeetingLink, dtstart: new Date(form.dtstart).toISOString(), dtend: new Date(form.dtend).toISOString() })
        });
        if (!res.ok) { const d = await res.json(); alert(d.error); setSaving(false); return; }
      }
      onSaved(); onClose();
    } catch (e) { alert('Failed to save'); }
    setSaving(false);
  };

  const deleteEvent = async () => {
    if (!editEvent || !window.confirm('Delete this event?')) return;
    await authFetch(`/api/calendar/events/${editEvent.uid}`, { method: 'DELETE', headers: { 'If-Match': `"${editEvent.etag}"` } });
    onSaved(); onClose();
  };

  return (
    <div className="calendar-modal modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}
        onFocus={e => { const t = e.target; setTimeout(() => t.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 250); }}>
        <div className="modal-header">
          <h3>{editEvent ? 'Edit Event' : 'New Event'}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="form-group">
          <label>Title</label>
          <input value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} autoFocus />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Start</label>
            <input type="datetime-local" value={form.dtstart} onChange={e => setForm({ ...form, dtstart: e.target.value })} />
          </div>
          <div className="form-group">
            <label>End</label>
            <input type="datetime-local" value={form.dtend} onChange={e => setForm({ ...form, dtend: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label>Location</label>
          <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Zoom, office, etc." />
        </div>
        <div className="form-group checkbox-group">
          <label>
            <input type="checkbox" checked={!!form.generateMeetingLink}
              onChange={e => setForm({ ...form, generateMeetingLink: e.target.checked || !!form.meetingLink })}
              disabled={!!form.meetingLink} />
            <span>Add NumFree video link</span>
          </label>
          {form.meetingLink ? (
            <div className="booking-share-url" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <a href={form.meetingLink} target="_blank" rel="noopener noreferrer">Join video meeting →</a>
              <button type="button" className="btn-copy-url" title="Copy link" onClick={() => navigator.clipboard.writeText(form.meetingLink)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
          ) : null}
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />
        </div>
        <div className="modal-actions">
          {editEvent && <button className="btn-delete-event" onClick={deleteEvent}>Delete</button>}
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={saveEvent} disabled={saving || !form.summary}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

const CalendarPage = ({ user }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('calendar'); // calendar | bookings | settings
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState(null);
  const bookingSlug = settings?.bookingSlug || user?.email?.split('@')[0] || '';
  const appBaseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://app.numfree.com';
  const appHost = typeof window !== 'undefined' ? window.location.host : 'app.numfree.com';
  const bookingPageUrl = `${appBaseUrl}/meet/${bookingSlug}`;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    loadEvents();
    loadBookings();
    loadSettings();
  }, []);

  useEffect(() => { loadEvents(); }, [year, month]);

  const loadEvents = async () => {
    const start = new Date(year, month, 1).toISOString();
    const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
    try {
      const res = await authFetch(`/api/calendar/events?start=${start}&end=${end}`);
      if (res.ok) setEvents(await res.json());
    } catch (e) {}
  };

  const loadBookings = async () => {
    try {
      const res = await authFetch('/api/calendar/bookings');
      if (res.ok) setBookings(await res.json());
    } catch (e) {}
  };

  const loadSettings = async () => {
    try {
      const res = await authFetch('/api/calendar/settings');
      if (res.ok) {
        const data = await res.json();
        setSettings({
          ...DEFAULT_SETTINGS,
          ...(data || {}),
          workingHours: { ...DEFAULT_WORKING_HOURS, ...((data && data.workingHours) || {}) }
        });
      }
    } catch (e) {}
  };


  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toISOString().slice(0, 10);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => eventDateKey(e, settings.timezone) === dateStr);
  };

  const openNewEvent = (day) => {
    setEditEvent(null);
    setSelectedDate(day);
    setShowModal(true);
  };

  const openEditEvent = (ev) => {
    setEditEvent(ev);
    setShowModal(true);
  };

  const saveSettings = async () => {
    setSettingsMessage(null);
    const wh = settings.workingHours || {};
    for (const d of WEEKDAYS) {
      const h = wh[d.key];
      if (h && h.start && h.end && h.end <= h.start) {
        setSettingsMessage({ type: 'error', text: `${d.label} end time must be after start time.` });
        return;
      }
    }
    setSaving(true);
    try {
      const res = await authFetch('/api/calendar/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSettingsMessage({ type: 'error', text: data.error || 'Could not save settings.' });
      } else {
        setSettingsMessage({ type: 'success', text: 'Calendar settings saved.' });
        loadSettings();
      }
    } catch (e) {
      setSettingsMessage({ type: 'error', text: e.message || 'Could not save settings.' });
    }
    setSaving(false);
  };

  const setWorkingDayEnabled = (key, enabled) => {
    const next = { ...(settings.workingHours || {}) };
    next[key] = enabled ? (next[key] || { start: '09:00', end: '17:00' }) : null;
    setSettings({ ...settings, workingHours: next });
  };

  const setWorkingDayTime = (key, field, value) => {
    const next = { ...(settings.workingHours || {}) };
    next[key] = { ...(next[key] || { start: '09:00', end: '17:00' }), [field]: value };
    setSettings({ ...settings, workingHours: next });
  };

  const updateBookingStatus = async (bookingId, status) => {
    await authFetch(`/api/calendar/bookings/${bookingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    loadBookings();
    loadEvents();
  };

  return (
    <div className="calendar-page">
      <GoogleSyncCard onSynced={() => loadEvents()} />

      <div className="calendar-tabs">
        <button className={tab === 'calendar' ? 'active' : ''} onClick={() => setTab('calendar')}>Calendar</button>
        <button className={tab === 'bookings' ? 'active' : ''} onClick={() => setTab('bookings')}>
          Bookings {bookings.filter(b => b.status === 'confirmed').length > 0 && <span className="badge">{bookings.filter(b => b.status === 'confirmed').length}</span>}
        </button>
        <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>Settings</button>
      </div>

      {tab === 'calendar' && (
        <div className="calendar-view">
          <div className="calendar-nav">
            <button onClick={prevMonth}>&lt;</button>
            <h2>{MONTHS[month]} {year}</h2>
            <button onClick={nextMonth}>&gt;</button>
            <button className="btn-today" onClick={today}>Today</button>
          </div>

          <div className="calendar-grid">
            {DAYS.map(d => <div key={d} className="cal-header">{d}</div>)}
            {Array.from({ length: firstDay }, (_, i) => <div key={'e' + i} className="cal-cell empty" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === todayStr;
              const dayEvents = getEventsForDay(day);
              return (
                <div key={day} className={`cal-cell ${isToday ? 'today' : ''}`} onClick={() => openNewEvent(day)}>
                  <span className="cal-day-num">{day}</span>
                  {dayEvents.slice(0, 3).map(ev => (
                    <div key={ev.uid} className="cal-event" style={{ backgroundColor: ev.color || '#2E7D86' }}
                      onClick={e => { e.stopPropagation(); openEditEvent(ev); }}>
                      {ev.summary}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="cal-more">+{dayEvents.length - 3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'bookings' && (
        <div className="bookings-view">
          <h3>Incoming Bookings</h3>
          {settings.bookingEnabled && (
            <div className="booking-url-bar">
              <span className="booking-url-label">Your booking page:</span>
              <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer" className="booking-url-link">
                {appHost}/meet/{bookingSlug || '...'}
              </a>
              <button className="btn-copy-url" onClick={() => {
                navigator.clipboard.writeText(bookingPageUrl);
              }} title="Copy link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
          )}
          {bookings.length === 0 ? (
            <p className="empty-msg">{settings.bookingEnabled ? 'No bookings yet. Share your booking link to get started!' : 'Enable your booking page in Settings first.'}</p>
          ) : (
            <div className="bookings-list">
              {bookings.map(b => (
                <div key={b.bookingId} className={`booking-card ${b.status}`}>
                  <div className="booking-info">
                    <strong>{b.guestName}</strong> — {b.guestEmail}
                    <div className="booking-time">{new Date(b.slot).toLocaleString()} ({b.duration}min)</div>
                    {b.notes && <div className="booking-notes">{b.notes}</div>}
                  </div>
                  <div className="booking-actions">
                    <span className={`booking-status ${b.status}`}>{b.status}</span>
                    {b.status === 'confirmed' && (
                      <>
                        <button className="btn-cancel-booking" onClick={() => updateBookingStatus(b.bookingId, 'cancelled')}>Cancel</button>
                        <button className="btn-noshow" onClick={() => updateBookingStatus(b.bookingId, 'no_show')}>No-show</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div className="settings-view">
          <h3>Calendar Settings</h3>
          <div className="form-group">
            <label>Timezone</label>
            <select value={settings.timezone || ''} onChange={e => setSettings({ ...settings, timezone: e.target.value })}>
              {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
                'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney',
                'Pacific/Auckland', 'UTC'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Slot Duration (minutes)</label>
            <select value={settings.slotDuration || 30} onChange={e => setSettings({ ...settings, slotDuration: parseInt(e.target.value) })}>
              {[15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Buffer Between Meetings (minutes)</label>
            <select value={settings.bufferMinutes || 15} onChange={e => setSettings({ ...settings, bufferMinutes: parseInt(e.target.value) })}>
              {[0, 5, 10, 15, 30].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Minimum Notice</label>
              <select value={settings.minNoticeMinutes ?? 60} onChange={e => setSettings({ ...settings, minNoticeMinutes: parseInt(e.target.value) })}>
                {[0, 15, 30, 60, 120, 240, 1440].map(d => <option key={d} value={d}>{d === 0 ? 'No minimum' : d < 1440 ? `${d} min` : '24 hours'}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Max Advance</label>
              <select value={settings.maxAdvanceDays ?? 60} onChange={e => setSettings({ ...settings, maxAdvanceDays: parseInt(e.target.value) })}>
                {[7, 14, 30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{d} days</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Hold Expiry</label>
            <select value={settings.holdExpiryMinutes ?? 1440} onChange={e => setSettings({ ...settings, holdExpiryMinutes: parseInt(e.target.value) })}>
              {[15, 30, 60, 240, 1440].map(d => <option key={d} value={d}>{d < 60 ? `${d} min` : d === 60 ? '1 hour' : d === 240 ? '4 hours' : '24 hours'}</option>)}
            </select>
          </div>
          <div className="working-hours">
            <div className="settings-section-label">Working hours</div>
            {WEEKDAYS.map(d => {
              const h = settings.workingHours && settings.workingHours[d.key];
              return (
                <div key={d.key} className={`working-day ${h ? 'enabled' : 'disabled'}`}>
                  <label className="working-day-toggle">
                    <input type="checkbox" checked={!!h} onChange={e => setWorkingDayEnabled(d.key, e.target.checked)} />
                    <span>{d.label}</span>
                  </label>
                  {h ? (
                    <div className="working-day-times">
                      <input type="time" value={h.start || '09:00'} onChange={e => setWorkingDayTime(d.key, 'start', e.target.value)} />
                      <span>to</span>
                      <input type="time" value={h.end || '17:00'} onChange={e => setWorkingDayTime(d.key, 'end', e.target.value)} />
                    </div>
                  ) : (
                    <span className="working-day-off">Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="notification-options">
            <div className="settings-section-label">Notifications</div>
            <div className="form-group checkbox-group">
              <label>
                <input type="checkbox" checked={settings.notifyViaEmail !== false} onChange={e => setSettings({ ...settings, notifyViaEmail: e.target.checked })} />
                <span>Email booking updates</span>
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input type="checkbox" checked={!!settings.notifyViaSms} onChange={e => setSettings({ ...settings, notifyViaSms: e.target.checked })} />
                <span>SMS booking updates when a notify number is available</span>
              </label>
            </div>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" checked={settings.bookingEnabled || false} onChange={e => setSettings({ ...settings, bookingEnabled: e.target.checked })} />
              <span>Enable public booking page</span>
            </label>
          </div>
          {settings.bookingEnabled && (
            <>
              <div className="form-group">
                <label>Booking Page Title</label>
                <input value={settings.bookingTitle || ''} onChange={e => setSettings({ ...settings, bookingTitle: e.target.value })} placeholder="Book a meeting" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={settings.bookingDescription || ''} onChange={e => setSettings({ ...settings, bookingDescription: e.target.value })} rows={3} />
              </div>
              <div className="form-group">
                <label>Booking URL slug</label>
                <div className="slug-preview">
                  {appHost}/meet/<input value={settings.bookingSlug || ''} onChange={e => setSettings({ ...settings, bookingSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder={user?.email?.split('@')[0] || 'yourname'} />
                </div>
                <div className="booking-share-url">
                  <span>Share this link:</span>
                  <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer">
                    {bookingPageUrl}
                  </a>
                </div>
              </div>
            </>
          )}
          {settingsMessage && <div className={`settings-message ${settingsMessage.type}`}>{settingsMessage.text}</div>}
          <button className="btn-save" onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
        </div>
      )}

      {/* Event Modal — isolated component with its own state (keeps typing off the grid). */}
      {showModal && (
        <EventModal
          key={editEvent ? editEvent.uid : ('new-' + selectedDate)}
          editEvent={editEvent}
          defaultDateStr={`${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDate || 1).padStart(2, '0')}`}
          onClose={() => setShowModal(false)}
          onSaved={loadEvents}
        />
      )}
    </div>
  );
};

export default CalendarPage;
